import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { get, post, put } from '../api';
import { useBrand } from '../components/Brand';
import { Problems } from '../components/Field';
import { Row } from '../components/FormRow';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { UnsavedChanges } from '../components/UnsavedChanges';
import { contractorTypeOptions } from '../contractorTypes';

interface Task {
  title: string;
  description: string;
}
interface Group {
  name: string;
  tasks: Task[];
}
interface Loaded {
  name: string;
  /** The types of work this checklist is the default for. */
  job_types: string[];
  used_by: number;
  groups: Group[];
}

const BLANK: Loaded = {
  name: '',
  job_types: [],
  used_by: 0,
  groups: [{ name: '', tasks: [{ title: '', description: '' }] }],
};

/** Move one item in a list up or down. */
function shift<T>(list: T[], i: number, by: -1 | 1): T[] {
  const j = i + by;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

export function ChecklistBuilder() {
  const { id } = useParams();
  const loaded = useLoad(
    () => (id ? get<Loaded>(`/api/checklists/${id}`) : Promise.resolve(null)),
    [id],
  );
  if (id && loaded.loading) return <p>Loading…</p>;
  if (id && loaded.error) {
    return (
      <p role="alert" className="error">
        {loaded.error}
      </p>
    );
  }
  return <Builder key={id ?? 'new'} id={id} initial={loaded.data ?? BLANK} />;
}

function Builder(props: { id?: string; initial: Loaded }) {
  const nav = useNavigate();
  const brand = useBrand();
  const typeOptions = contractorTypeOptions(brand?.slug ?? '');
  const initialTypes = props.initial.job_types ?? [];
  const [name, setName] = useState(props.initial.name);
  const [jobTypes, setJobTypes] = useState<string[]>(initialTypes);
  const [groups, setGroups] = useState<Group[]>(
    props.initial.groups.length ? props.initial.groups : BLANK.groups,
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const setGroup = (i: number, patch: Partial<Group>) =>
    setGroups(groups.map((g, n) => (n === i ? { ...g, ...patch } : g)));
  const setTask = (gi: number, ti: number, patch: Partial<Task>) =>
    setGroup(gi, { tasks: groups[gi]!.tasks.map((t, n) => (n === ti ? { ...t, ...patch } : t)) });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblems([]);
    setFields({});
    try {
      const body = { name, scope: 'library', job_types: jobTypes, groups };
      if (props.id) await put(`/api/checklists/${props.id}`, body);
      else await post('/api/checklists', body);
      nav('/checklists');
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
      setBusy(false);
    }
  }

  const dirty =
    !busy &&
    (name !== props.initial.name ||
      jobTypes.join(',') !== initialTypes.join(',') ||
      JSON.stringify(groups) !==
        JSON.stringify(props.initial.groups.length ? props.initial.groups : BLANK.groups));

  return (
    <form onSubmit={submit} className="stack narrow" noValidate>
      <UnsavedChanges when={dirty} />
      <p>
        <Link to="/checklists">← Checklists</Link>
      </p>
      <h1>{props.id ? 'Edit checklist' : 'Add checklist'}</h1>
      <Problems problems={problems} />
      <section className="panel" aria-label="Checklist">
        <Row label="Name" error={fields.name}>
          <input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        </Row>
        <Row
          label="Default for"
          group
          error={fields.job_types}
          hint="Visits of these types of work get this checklist unless their assignment picks another."
        >
          {/* A type no longer offered still shows, so it can be taken off. */}
          {[
            ...typeOptions,
            ...jobTypes
              .filter((v) => !typeOptions.some((o) => o.value === v))
              .map((v) => ({ value: v, label: v })),
          ].map((o) => (
            <label key={o.value} className="check">
              <input
                type="checkbox"
                checked={jobTypes.includes(o.value)}
                onChange={(e) =>
                  setJobTypes(
                    e.target.checked
                      ? [...jobTypes, o.value]
                      : jobTypes.filter((v) => v !== o.value),
                  )
                }
              />
              {o.label}
            </label>
          ))}
        </Row>
        <Row label="Applies to">
          <span className="muted">
            {props.initial.used_by === 0
              ? 'No assignments use this checklist yet.'
              : `${props.initial.used_by} ${props.initial.used_by === 1 ? 'assignment uses' : 'assignments use'} this checklist.`}{' '}
            Changes apply to future visits only.
          </span>
        </Row>
      </section>
      {fields.items && <p className="error">{fields.items}</p>}

      {groups.map((g, gi) => (
        <section key={gi} className="group-card" aria-label={`Section ${gi + 1}`}>
          <div className="group-head">
            <input
              aria-label={`Section ${gi + 1} name`}
              placeholder="Section name (optional)"
              value={g.name}
              onChange={(e) => setGroup(gi, { name: e.target.value })}
            />
            <button
              type="button"
              className="quiet"
              disabled={gi === 0}
              onClick={() => setGroups(shift(groups, gi, -1))}
            >
              ↑<span className="sr-only"> Move section {gi + 1} up</span>
            </button>
            <button
              type="button"
              className="quiet"
              disabled={gi === groups.length - 1}
              onClick={() => setGroups(shift(groups, gi, 1))}
            >
              ↓<span className="sr-only"> Move section {gi + 1} down</span>
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => setGroups(groups.filter((_, n) => n !== gi))}
            >
              Remove section<span className="sr-only"> {gi + 1}</span>
            </button>
          </div>
          <ul className="plain group-tasks">
            {g.tasks.map((t, ti) => (
              <li key={ti} className="task-row">
                <input
                  aria-label={`Section ${gi + 1} task ${ti + 1}`}
                  value={t.title}
                  onChange={(e) => setTask(gi, ti, { title: e.target.value })}
                />
                <button
                  type="button"
                  className="quiet"
                  disabled={ti === 0}
                  onClick={() => setGroup(gi, { tasks: shift(g.tasks, ti, -1) })}
                >
                  ↑<span className="sr-only"> Move task {ti + 1} up</span>
                </button>
                <button
                  type="button"
                  className="quiet"
                  disabled={ti === g.tasks.length - 1}
                  onClick={() => setGroup(gi, { tasks: shift(g.tasks, ti, 1) })}
                >
                  ↓<span className="sr-only"> Move task {ti + 1} down</span>
                </button>
                <button
                  type="button"
                  className="quiet"
                  onClick={() => setGroup(gi, { tasks: g.tasks.filter((_, n) => n !== ti) })}
                >
                  ×<span className="sr-only"> Remove task {ti + 1}</span>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="quiet add-tag"
            onClick={() => setGroup(gi, { tasks: [...g.tasks, { title: '', description: '' }] })}
          >
            + Add task<span className="sr-only"> to section {gi + 1}</span>
          </button>
        </section>
      ))}
      <button
        type="button"
        className="quiet add-tag"
        onClick={() =>
          setGroups([...groups, { name: '', tasks: [{ title: '', description: '' }] }])
        }
      >
        + Add section
      </button>

      <div className="row start">
        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save checklist'}
        </button>
        <Link className="button quiet-button" to="/checklists">
          Cancel
        </Link>
      </div>
    </form>
  );
}
